/**
 * Каталог: отбор, поиск словами, сравнение.
 *
 * Отбирает всегда код, по локальной базе — поэтому работает без сети и даёт
 * на один и тот же вопрос один и тот же ответ. Модель участвует ровно в одном
 * месте: переводит произнесённую фразу в тот же самый набор условий, который
 * человек мог бы выставить руками. Что она поняла, видно строкой условий —
 * и любое из них снимается пальцем.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../db';
import { apiFetch } from '../sync';
import { loadRows, loadThumbs } from '../lib/catalog';
import { releasePictures } from '../lib/pictures';
import { CollectionSheet } from './StoneActions';
import { shareTable } from '../lib/export';
import {
  matchStone, sortRows, filterChips, filterIsEmpty, rowPerCt, rowWeight, rowCurrency, type Range,
  parseMoney, formatMoney, plural, STATUSES, STATUS_LABEL,
  SORT_LABEL, ATTRIBUTES, ATTRIBUTE_ORDER,
  type Filter, type StoneRow, type SortKey, type Status, type AttributeKey,
} from '@core';

// ── общие мелочи ────────────────────────────────────────────────────────────

const CONF_SHORT: Record<string, string> = {
  certified: 'сертификат',
  assessed: 'оценка',
  claimed: 'со слов',
  low: 'проверить',
};

/**
 * Деньги в списке.
 *
 * Цена за карат округляется до целой единицы валюты — тем же правилом, что в
 * карточке камня. Иначе одно и то же число выглядит по-разному в двух местах
 * ($1,556 и $1,555.56), и человек начинает искать разницу там, где её нет.
 */
function money(n: number | null | undefined, currency = 'USD'): string {
  if (n == null) return '—';
  const m = parseMoney(Math.round(n), currency);
  return m ? formatMoney(m) : String(n);
}

/** Короткая подпись камня: то, по чему его узнают в списке. */
function caption(r: StoneRow): string {
  return [
    r.values.get('stone_type')?.text ?? r.values.get('variety')?.text,
    weightCaption(r),
    r.values.get('color')?.text,
  ].filter(Boolean).join(' · ') || 'без описания';
}

/**
 * Вес партии — это диапазон, а не число. Показать его средним значением
 * значит соврать: камня весом «1.75 ct» в такой партии может не быть вовсе.
 */
function weightCaption(r: StoneRow): string | null {
  const one = r.values.get('weight_ct')?.num;
  if (one != null) return `${Number(Number(one).toFixed(2))} ct`;
  const a = r.values.get('weight_min_ct')?.num;
  const b = r.values.get('weight_max_ct')?.num;
  if (a != null && b != null) return `${a}—${b} ct`;
  return a != null ? `от ${a} ct` : b != null ? `до ${b} ct` : null;
}

/** Значения, встречающиеся в этом каталоге — фильтр предлагает только их. */
function vocabulary(rows: StoneRow[], attr: string, limit = 14): string[] {
  const count = new Map<string, number>();
  for (const r of rows) {
    const v = r.values.get(attr)?.text;
    if (!v) continue;
    count.set(v, (count.get(v) ?? 0) + 1);
  }
  return [...count.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([v]) => v);
}

function toggle<T>(list: T[] | undefined, value: T): T[] | undefined {
  const set = new Set(list ?? []);
  set.has(value) ? set.delete(value) : set.add(value);
  return set.size ? [...set] : undefined;
}

/**
 * Номер для плитки. Один только порядковый номер обманчив: 0002 есть у
 * каждого поставщика, и три разных камня выглядели бы одинаково.
 */
export function shortNo(no: string): string {
  const parts = no.split('-');
  if (parts.length < 2) return no;
  return `${parts[parts.length - 2].slice(0, 3)} ${parts[parts.length - 1]}`;
}

const num = (s: string): number | null => {
  const v = Number(String(s).replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(v) && s.trim() !== '' ? v : null;
};

// ── каталог ─────────────────────────────────────────────────────────────────

export function Browse({ initial, onOpenStone, onCompare }: {
  initial?: Filter;
  onOpenStone: (id: string) => void;
  onCompare: (ids: string[]) => void;
}) {
  const [rows, setRows] = useState<StoneRow[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<Filter>(initial ?? {});
  const [text, setText] = useState(initial?.q ?? '');
  const [sheet, setSheet] = useState(false);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState('');
  const [grid, setGrid] = useState(true);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [supplierNames, setSupplierNames] = useState<Record<string, string>>({});
  const [collecting, setCollecting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState('');

  useEffect(() => {
    void (async () => {
      setRows(await loadRows());
      const sups = await db.suppliers.toArray();
      setSupplierNames(Object.fromEntries(sups.map((s) => [s.id, s.companyName])));
    })();
    return () => releasePictures();
  }, []);

  // Свободный текст ищется сразу и локально: это быстро и предсказуемо.
  const active: Filter = useMemo(() => ({ ...filter, q: text.trim() || undefined }), [filter, text]);

  const found = useMemo(
    () => sortRows(rows.filter((r) => matchStone(r, active)), active.sort ?? 'recent'),
    [rows, active],
  );

  useEffect(() => {
    void loadThumbs(found.slice(0, 60).map((r) => r.id)).then(setThumbs);
  }, [found]);

  /** Перевести фразу в условия. Всё, что модель поняла, остаётся на виду. */
  async function ask() {
    const phrase = text.trim();
    if (!phrase) return;
    setAsking(true);
    setAskError('');
    try {
      const res = await apiFetch('/api/parse-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: phrase }),
      });
      if (!res.ok) throw new Error(res.status === 401 ? 'нужен вход' : `сервер ответил ${res.status}`);
      const d = (await res.json()) as { filter: Filter };
      const f = d.filter ?? {};
      setFilter({ ...f, q: f.q });
      setText(f.q ?? '');
    } catch (e: any) {
      setAskError(`Не разобрал фразу: ${e?.message ?? e}. Отбор кнопками работает и без сети.`);
    } finally {
      setAsking(false);
    }
  }

  const chips = filterChips(filter, { supplier: (id) => supplierNames[id] });

  /*
   * Валюты в отборе. Курсов мы не знаем и знать не хотим — выдуманный курс
   * хуже честной оговорки. Но молча сортировать баты вместе с долларами по
   * колонке «дешевле за карат» нельзя: список получится правдоподобным и
   * неверным.
   */
  const currencies = useMemo(() => {
    const set = new Set<string>();
    for (const r of found) if (rowPerCt(r) != null) set.add(rowCurrency(r));
    return [...set];
  }, [found]);
  const phraseLike = text.trim().split(/\s+/).length > 1;

  function pick(id: string) {
    // Потолок в четыре — у сравнения, не у выбора: подборка бывает и на двадцать.
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  return (
    <div className={picking && picked.length ? 'app' : 'app tabbed'}>
      <div className="bar">
        <h1 className="t">Каталог</h1>
        <p className="meta">
          {filterIsEmpty(active)
            ? `${rows.length} ${plural(rows.length, 'камень', 'камня', 'камней')}`
            : `${found.length} из ${rows.length}`}
        </p>
      </div>

      <div className="scroll">
        <div className="seek">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && phraseLike) void ask(); }}
            placeholder="Номер, поставщик или фраза"
            inputMode="search" enterKeyHint="search"
          />
          {text && (
            <button className="seek-x" onClick={() => { setText(''); setAskError(''); }} aria-label="Очистить">✕</button>
          )}
        </div>

        {phraseLike && (
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}
                  disabled={asking} onClick={() => void ask()}>
            {asking ? 'Разбираю…' : 'Понять как запрос'}
          </button>
        )}
        {askError && <div className="memo warn">{askError}</div>}

        {currencies.length > 1 && (
          <div className="memo warn" style={{ marginTop: 10 }}>
            <b>Цены в разных валютах: {currencies.join(', ')}.</b> Сортировка и
            отбор по цене считают голые числа, без пересчёта по курсу — сравнивайте
            внутри одной валюты.
          </div>
        )}

        <div className="states" style={{ marginTop: 12 }}>
          <button className="state-btn" aria-pressed={!filterIsEmpty(filter)} onClick={() => setSheet(true)}>
            Отбор{chips.length ? ` · ${chips.length}` : ''}
          </button>
          <button className="state-btn" aria-pressed={grid} onClick={() => setGrid(true)}>Карта</button>
          <button className="state-btn" aria-pressed={!grid} onClick={() => setGrid(false)}>Списком</button>
          <button className="state-btn" aria-pressed={picking}
                  onClick={() => { setPicking((p) => !p); setPicked([]); }}>
            Сравнить
          </button>
          {/* Выгружается ровно то, что сейчас отобрано: «пришли мне сапфиры
              до пяти тысяч» — это и есть файл, который ждут в офисе. */}
          <button className="state-btn" disabled={!found.length || exporting}
                  onClick={() => {
                    setExporting(true);
                    void shareTable(found)
                      .then((how) => setExported(how === 'shared' ? 'Отправлено' : 'Файл сохранён'))
                      .catch(() => setExported('Не вышло выгрузить'))
                      .finally(() => {
                        setExporting(false);
                        setTimeout(() => setExported(''), 3000);
                      });
                  }}>
            {exporting ? 'Готовлю…' : exported || `Таблица · ${found.length}`}
          </button>
        </div>

        {chips.length > 0 && (
          <div className="terms">
            {chips.map((c) => <span className="term" key={c}>{c}</span>)}
            <button className="term drop" onClick={() => { setFilter({}); setText(''); }}>
              снять всё ✕
            </button>
          </div>
        )}

        {picking && (
          <div className="memo" style={{ marginTop: 10 }}>
            Отметьте камни: до четырёх — сравнить рядом, сколько угодно —
            отправить покупателю одной страницей.
          </div>
        )}

        {found.length === 0 ? (
          <div className="blankslate">
            <b>{rows.length ? 'Под этот отбор ничего нет' : 'Пока ничего не снято'}</b>
            {rows.length
              ? 'Снимите условие — или спросите иначе.'
              : 'Заведите стенд с визитки и начните снимать.'}
          </div>
        ) : grid ? (
          <div className="shelf" style={{ marginTop: 14 }}>
            {found.map((r) => (
              <button key={r.id} className="stone" data-picked={picked.includes(r.id) ? '1' : undefined}
                      onClick={() => (picking ? pick(r.id) : onOpenStone(r.id))}
                      aria-label={`${r.no}, ${caption(r)}`}>
                {thumbs[r.id]
                  ? <img src={thumbs[r.id]} alt="" />
                  : <span className="blank">{r.kind === 'parcel' ? '⁘' : '◆'}</span>}
                {r.status !== 'viewed' && <span className={`state st-${r.status}`} />}
                <span className="tag">{shortNo(r.no)}</span>
                {picking && picked.includes(r.id) && (
                  <span className="pick">{picked.indexOf(r.id) + 1}</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="list" style={{ marginTop: 14 }}>
            {found.map((r) => {
              const perCt = rowPerCt(r);
              return (
                <button key={r.id} className="row" data-picked={picked.includes(r.id) ? '1' : undefined}
                        onClick={() => (picking ? pick(r.id) : onOpenStone(r.id))}>
                  <span className="mark">
                    {thumbs[r.id] ? <img src={thumbs[r.id]} alt="" /> : (r.kind === 'parcel' ? '⁘' : '◆')}
                  </span>
                  {/* Вид и вес — первой строкой, цвет и номер — второй.
                      В одной строке цвет обрезался первым, а он важнее
                      полного номера: по нему камень и узнают. */}
                  <span className="grow">
                    <span className="nm">
                      {[r.values.get('stone_type')?.text ?? r.values.get('variety')?.text,
                        weightCaption(r)].filter(Boolean).join(' · ') || 'без описания'}
                    </span>
                    <span className="sub">
                      {[r.values.get('color')?.text, shortNo(r.no), r.supplierName]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {/* Цена и то, за что она — двумя строками. В одну строку
                      «$1,139/ct» читается как одно длинное число. */}
                  <span className="num">
                    <b>{perCt != null
                      ? money(perCt, rowCurrency(r))
                      : money(r.values.get('asking_price')?.num, rowCurrency(r))}</b>
                    {(perCt != null || r.values.get('asking_price')?.num != null) && (
                      <i>{perCt != null ? 'за карат' : 'за камень'}</i>
                    )}
                  </span>
                  {r.status !== 'viewed' && <span className={`state st-${r.status}`} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {picking && picked.length >= 1 && (
        <div className="dock">
          {picked.length >= 2 && picked.length <= 4 && (
            <button className="btn btn-secondary" onClick={() => onCompare(picked)}>
              Сравнить {picked.length}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setCollecting(true)}>
            Покупателю · {picked.length}
          </button>
        </div>
      )}

      {collecting && (
        <CollectionSheet
          ids={picked}
          onClose={() => setCollecting(false)}
          onDone={() => { setPicking(false); setPicked([]); }}
        />
      )}

      {sheet && (
        <FilterSheet
          rows={rows} filter={filter} supplierNames={supplierNames}
          onApply={(f) => { setFilter(f); setSheet(false); }}
          onClose={() => setSheet(false)}
        />
      )}
    </div>
  );
}

// ── лист отбора ─────────────────────────────────────────────────────────────

/**
 * Готовые диапазоны вместо клавиатуры.
 *
 * У стенда никто не набирает «2.5»: телефон в одной руке, лупа в другой.
 * Пороги взяты из того, как торгуются камни: до карата — мелочь, 1–3 — ходовой
 * размер, 3–5 — крупный, дальше — отдельный разговор. Поля «от/до» остаются
 * ниже для точного случая, но обычный путь — одно нажатие.
 */
const WEIGHT_STEPS: Array<[string, Range]> = [
  ['до 1 ct', { to: 1 }],
  ['1–2', { from: 1, to: 2 }],
  ['2–3', { from: 2, to: 3 }],
  ['3–5', { from: 3, to: 5 }],
  ['от 5', { from: 5 }],
];
const PERCT_STEPS: Array<[string, Range]> = [
  ['до $500', { to: 500 }],
  ['$500–2 тыс', { from: 500, to: 2000 }],
  ['$2–5 тыс', { from: 2000, to: 5000 }],
  ['$5–10 тыс', { from: 5000, to: 10000 }],
  ['от $10 тыс', { from: 10000 }],
];
const PRICE_STEPS: Array<[string, Range]> = [
  ['до $1 тыс', { to: 1000 }],
  ['$1–5 тыс', { from: 1000, to: 5000 }],
  ['$5–20 тыс', { from: 5000, to: 20000 }],
  ['от $20 тыс', { from: 20000 }],
];

const sameRange = (a?: Range, b?: Range) =>
  (a?.from ?? null) === (b?.from ?? null) && (a?.to ?? null) === (b?.to ?? null);

/**
 * Диапазон кнопками, с точным вводом на запас.
 *
 * Компонент верхнего уровня, а не замыкание внутри листа: замыкание
 * пересоздавалось бы на каждый рендер, и поле теряло бы фокус после первой
 * же цифры. Точные поля неуправляемые по той же причине — «2.» по пути к
 * «2.5» не должно превращаться в «2»; нажатие пресета перезаводит их ключом.
 */
function RangeGroup({ title, steps, value, onSet }: {
  title: string; steps: Array<[string, Range]>;
  value?: Range; onSet: (r?: Range) => void;
}) {
  const [tick, setTick] = useState(0);
  return (
    <>
      <div className="head"><h3>{title}</h3><span /></div>
      <div className="states" style={{ marginTop: 0 }}>
        {steps.map(([cap, r]) => (
          <button key={cap} className="state-btn" aria-pressed={sameRange(value, r)}
                  onClick={() => { onSet(sameRange(value, r) ? undefined : r); setTick((t) => t + 1); }}>
            {cap}
          </button>
        ))}
      </div>
      <div className="pair exact" key={tick}>
        <label className="field"><span>точно от</span>
          <input inputMode="decimal" defaultValue={value?.from ?? ''}
                 onChange={(e) => onSet({ ...value, from: num(e.target.value) })} /></label>
        <label className="field"><span>до</span>
          <input inputMode="decimal" defaultValue={value?.to ?? ''}
                 onChange={(e) => onSet({ ...value, to: num(e.target.value) })} /></label>
      </div>
    </>
  );
}

function FilterSheet({ rows, filter, supplierNames, onApply, onClose }: {
  rows: StoneRow[];
  filter: Filter;
  supplierNames: Record<string, string>;
  onApply: (f: Filter) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState<Filter>(filter);
  const set = (patch: Partial<Filter>) => setF((prev) => ({ ...prev, ...patch }));

  const types = useMemo(() => vocabulary(rows, 'stone_type'), [rows]);
  const colors = useMemo(() => vocabulary(rows, 'color'), [rows]);
  const origins = useMemo(() => vocabulary(rows, 'origin'), [rows]);
  const labs = useMemo(() => vocabulary(rows, 'lab', 8), [rows]);
  const suppliers = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of rows) seen.set(r.supplierId, (seen.get(r.supplierId) ?? 0) + 1);
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([id]) => id);
  }, [rows]);

  const preview = rows.filter((r) => matchStone(r, f)).length;

  const Group = ({ title, list, chosen, onPick }: {
    title: string; list: string[]; chosen?: string[]; onPick: (v: string) => void;
  }) => list.length === 0 ? null : (
    <>
      <div className="head"><h3>{title}</h3><span>{chosen?.length ? chosen.length : ''}</span></div>
      <div className="states" style={{ marginTop: 0 }}>
        {list.map((v) => (
          <button key={v} className="state-btn" aria-pressed={chosen?.includes(v) ?? false}
                  onClick={() => onPick(v)}>{v}</button>
        ))}
      </div>
    </>
  );

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <div className="head" style={{ marginTop: 0 }}>
          <h3>Отбор</h3>
          <span>{preview} {plural(preview, 'камень', 'камня', 'камней')}</span>
        </div>

        <Group title="Вид" list={types} chosen={f.types} onPick={(v) => set({ types: toggle(f.types, v) })} />
        <Group title="Цвет" list={colors} chosen={f.colors} onPick={(v) => set({ colors: toggle(f.colors, v) })} />
        <Group title="Происхождение" list={origins} chosen={f.origins} onPick={(v) => set({ origins: toggle(f.origins, v) })} />
        <Group title="Лаборатория" list={labs} chosen={f.labs} onPick={(v) => set({ labs: toggle(f.labs, v) })} />

        <RangeGroup title="Вес, ct" steps={WEIGHT_STEPS}
                    value={f.weightCt} onSet={(r) => set({ weightCt: r })} />

        {/* Цена за карат — первой: у стенда торгуются за карат, а не за камень. */}
        <RangeGroup title="Цена за карат, $" steps={PERCT_STEPS}
                    value={f.perCt} onSet={(r) => set({ perCt: r })} />

        <RangeGroup title="Цена за камень, $" steps={PRICE_STEPS}
                    value={f.price} onSet={(r) => set({ price: r })} />

        <div className="head"><h3>Отчёт лаборатории</h3><span /></div>
        <div className="states" style={{ marginTop: 0 }}>
          {([['certified', 'проверен'], ['claimed', 'не проверен'], ['none', 'без отчёта']] as const).map(([v, cap]) => (
            <button key={v} className="state-btn" aria-pressed={f.cert === v}
                    onClick={() => set({ cert: f.cert === v ? undefined : v })}>{cap}</button>
          ))}
        </div>

        <div className="head"><h3>Обработка</h3><span /></div>
        <div className="states" style={{ marginTop: 0 }}>
          {([['untreated', 'без облагораживания'], ['treated', 'с обработкой']] as const).map(([v, cap]) => (
            <button key={v} className="state-btn" aria-pressed={f.treatment === v}
                    onClick={() => set({ treatment: f.treatment === v ? undefined : v })}>{cap}</button>
          ))}
        </div>

        <div className="head"><h3>Решение</h3><span /></div>
        <div className="states" style={{ marginTop: 0 }}>
          {STATUSES.map((s) => (
            <button key={s} className="state-btn" aria-pressed={f.statuses?.includes(s) ?? false}
                    onClick={() => set({ statuses: toggle(f.statuses, s) as Status[] | undefined })}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="head"><h3>Что снято</h3><span /></div>
        <div className="states" style={{ marginTop: 0 }}>
          {([['stone', 'поштучно'], ['parcel', 'партии']] as const).map(([v, cap]) => (
            <button key={v} className="state-btn" aria-pressed={f.kind === v}
                    onClick={() => set({ kind: f.kind === v ? undefined : v })}>{cap}</button>
          ))}
        </div>

        {suppliers.length > 1 && (
          <Group title="Поставщик" list={suppliers.map((id) => supplierNames[id] ?? id)}
                 chosen={f.supplierIds?.map((id) => supplierNames[id] ?? id)}
                 onPick={(name) => {
                   const id = suppliers.find((s) => (supplierNames[s] ?? s) === name);
                   if (id) set({ supplierIds: toggle(f.supplierIds, id) });
                 }} />
        )}

        <div className="head"><h3>Порядок</h3><span /></div>
        <div className="states" style={{ marginTop: 0 }}>
          {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
            <button key={k} className="state-btn" aria-pressed={(f.sort ?? 'recent') === k}
                    onClick={() => set({ sort: k })}>{SORT_LABEL[k]}</button>
          ))}
        </div>

        <div className="dock" style={{ position: 'static', padding: '18px 0 0' }}>
          <button className="btn btn-secondary" onClick={() => setF({ sort: f.sort })}>Сбросить</button>
          <button className="btn btn-primary" onClick={() => onApply(f)}>Показать {preview}</button>
        </div>
      </div>
    </>
  );
}

// ── сравнение ───────────────────────────────────────────────────────────────

/**
 * Камни рядом.
 *
 * Показываем не только значения, но и откуда каждое взялось: сравнивать
 * сертифицированный цвет с цветом со слов поставщика — значит сравнивать
 * разные по надёжности вещи, и человек должен это видеть. Строки, где все
 * значения совпали, приглушены — глазу нужны различия.
 */
export function Compare({ ids, onBack, onOpenStone }: {
  ids: string[]; onBack: () => void; onOpenStone: (id: string) => void;
}) {
  const [rows, setRows] = useState<StoneRow[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [onlyDiff, setOnlyDiff] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void (async () => {
      const all = await loadRows();
      const chosen = ids.map((id) => all.find((r) => r.id === id)).filter(Boolean) as StoneRow[];
      setRows(chosen);
      setThumbs(await loadThumbs(ids));
    })();
    return () => releasePictures();
  }, [ids.join(',')]);

  // Цена и цена за карат вынесены наверх отдельными строками — в общем
  // списке они были бы вторым, менее внятным показом того же самого.
  const attrs = useMemo(() => {
    const present = new Set<string>();
    for (const r of rows) for (const k of r.values.keys()) present.add(k);
    const shownAbove = new Set(['asking_price', 'asking_price_per_ct', 'asking_price_per_ct_lot', 'currency']);
    return ATTRIBUTE_ORDER.filter((a) => present.has(a) && !shownAbove.has(a));
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="app">
        <div className="bar"><button className="crumb" onClick={onBack}>‹ Назад</button></div>
        <div className="blankslate">Нечего сравнивать.</div>
      </div>
    );
  }

  const same = (attr: string) => {
    const vals = rows.map((r) => (r.values.get(attr)?.text ?? '').toLowerCase().trim());
    return vals.every((v) => v === vals[0]);
  };

  /**
   * Один источник на всю строку — или разные.
   *
   * Когда у всех трёх камней цвет со слов поставщика, повторять это трижды
   * незачем. А вот когда у одного цвет из сертификата, а у другого со слов —
   * это и есть самое важное в сравнении, и тогда метка стоит у каждого.
   */
  const sharedConfidence = (attr: string): string | null => {
    const list = rows.map((r) => r.values.get(attr)?.confidence).filter(Boolean) as string[];
    if (list.length !== rows.length || list.length === 0) return null;
    return list.every((c) => c === list[0]) ? list[0] : null;
  };

  const shown = onlyDiff ? attrs.filter((a) => !same(a)) : attrs;

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Каталог</button>
        <h1 className="t">Рядом</h1>
        <p className="meta">{rows.length} {plural(rows.length, 'камень', 'камня', 'камней')} · листайте вбок</p>
      </div>

      <div className="scroll">
        <div className="states" style={{ marginTop: 0 }}>
          <button className="state-btn" aria-pressed={!onlyDiff} onClick={() => setOnlyDiff(false)}>Все поля</button>
          <button className="state-btn" aria-pressed={onlyDiff} onClick={() => setOnlyDiff(true)}>Только различия</button>
        </div>

        <div className="cmp" ref={scroller}>
          <table>
            <thead>
              <tr>
                <th className="side" />
                {rows.map((r) => (
                  <th key={r.id}>
                    <button className="cmp-head" onClick={() => onOpenStone(r.id)}>
                      {thumbs[r.id]
                        ? <img src={thumbs[r.id]} alt="" />
                        : <span className="blank">{r.kind === 'parcel' ? '⁘' : '◆'}</span>}
                      <b>{shortNo(r.no)}</b>
                      <span>{r.supplierName ?? '—'}</span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="key">
                <th className="side">Цена за карат</th>
                {rows.map((r) => {
                  const v = rowPerCt(r);
                  const quoted = r.values.get('asking_price_per_ct')?.num != null;
                  const lotOnly = !quoted && r.values.get('asking_price_per_ct_lot')?.num != null;
                  return (
                    <td key={r.id}>
                      <b>{money(v, rowCurrency(r))}</b>
                      {v != null && <i>{quoted ? 'со слов' : lotOnly ? 'лотом' : 'вычислено'}</i>}
                    </td>
                  );
                })}
              </tr>
              {/* Строка появляется, только если хоть у одного камня цена лотом
                  названа: у штучных её не бывает, и пустая строка врала бы. */}
              {rows.some((r) => r.values.get('asking_price_per_ct_lot')?.num != null) && (
                <tr className="key">
                  <th className="side">За карат лотом</th>
                  {rows.map((r) => (
                    <td key={r.id}><b>{money(r.values.get('asking_price_per_ct_lot')?.num, rowCurrency(r))}</b></td>
                  ))}
                </tr>
              )}
              <tr className="key">
                <th className="side">Цена</th>
                {rows.map((r) => <td key={r.id}><b>{money(r.values.get('asking_price')?.num, rowCurrency(r))}</b></td>)}
              </tr>
              <tr>
                <th className="side">Решение</th>
                {rows.map((r) => (
                  <td key={r.id}>{STATUS_LABEL[r.status as Status] ?? r.status}</td>
                ))}
              </tr>

              {shown.map((attr) => {
                const shared = sharedConfidence(attr);
                return (
                <tr key={attr} className={same(attr) ? 'same' : undefined}>
                  <th className="side">
                    {ATTRIBUTES[attr as AttributeKey]?.label ?? attr}
                    {shared && <i className={`src src-${shared}`}>{CONF_SHORT[shared] ?? shared}</i>}
                  </th>
                  {rows.map((r) => {
                    const f = r.values.get(attr);
                    const unit = (ATTRIBUTES[attr as AttributeKey] as { unit?: string }).unit;
                    return (
                      <td key={r.id}>
                        {f?.text ? (
                          <>
                            {f.text}{unit ? ` ${unit}` : ''}
                            {f.confidence && !shared && (
                              <i className={`src src-${f.confidence}`}>{CONF_SHORT[f.confidence] ?? f.confidence}</i>
                            )}
                          </>
                        ) : <span className="none">—</span>}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {onlyDiff && shown.length === 0 && (
          <div className="blankslate">
            <b>Поля совпадают полностью</b>
            Это повод проверить, не один ли это камень, снятый дважды.
          </div>
        )}
      </div>
    </div>
  );
}
